import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { SectionClouds } from "./SectionClouds";

type Audience = "startup" | "scaleup" | "prototype";

interface Step {
  title: string;
  body: string;
  bullets: string[];
}

const audienceOrder: Audience[] = ["startup", "scaleup", "prototype"];

function isAudience(value: string | null): value is Audience {
  return value !== null && (audienceOrder as string[]).includes(value);
}

function readInitialAudience(): Audience {
  if (typeof window === "undefined") return "startup";
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("via");
    if (isAudience(fromUrl)) return fromUrl;
    const fromStorage = window.localStorage.getItem("holymoly:via");
    if (isAudience(fromStorage)) return fromStorage;
  } catch {
    // ignore — fall through to default
  }
  return "startup";
}

export function Process() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = React.useState(false);
  const [active, setActive] = React.useState<Audience>("startup");
  const ref = React.useRef<HTMLDivElement>(null);

  // Apply the ?via= / localStorage preference after mount. Reading it inside
  // the useState initializer would make the hydration render diverge from the
  // prerendered HTML (which is always the "startup" track).
  React.useEffect(() => {
    const initial = readInitialAudience();
    if (initial !== "startup") setActive(initial);
  }, []);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Audience>).detail;
      if (audienceOrder.includes(detail)) setActive(detail);
    };
    window.addEventListener("holymoly:via", handler);
    return () => window.removeEventListener("holymoly:via", handler);
  }, []);

  const handleSelect = (audience: Audience) => {
    setActive(audience);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("holymoly:via", audience);
      } catch {
        // ignore
      }
    }
    // Update the `?via=` search param through the router's own navigate()
    // instead of a raw `window.history.replaceState`. @tanstack/history
    // monkey-patches `window.history.replaceState`, so a raw call still
    // notifies the router's Transitioner, which reloads the route and re-runs
    // scroll restoration (scrollRestoration: true, see router.tsx). With no
    // cached scroll entry for the freshly-changed URL, that fell through to
    // scrolling to whatever hash was still in the URL (e.g. a stale `#team`
    // left over from an earlier navbar click) — see
    // node_modules/@tanstack/router-core/dist/esm/scroll-restoration.js.
    // Going through navigate() lets us opt out explicitly: `resetScroll:
    // false` skips the post-navigation scroll restore entirely, and omitting
    // `hash` (routed here as `hash: ""`) drops the stale fragment instead of
    // carrying it forward, so there's nothing left for handleHashScroll to
    // act on even if it ran.
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, via: audience }),
      hash: "",
      replace: true,
      resetScroll: false,
      hashScrollIntoView: false,
    });
  };

  const steps = t(`landing.process.tracks.${active}`, {
    returnObjects: true,
  }) as Step[];
  const localizedHref = useLocalizedHref();

  return (
    <section ref={ref} id="process" className="frame bg-page-bg">
      <SectionClouds variant="process" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">03 —</span> {t("landing.process.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.process.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.process.lede")}</p>

          <div className="mt-10 flex justify-center">
            <div
              role="tablist"
              aria-label={t("landing.process.trackTabsLabel")}
              className="inline-flex flex-wrap items-center gap-1 rounded-full border border-line bg-surface-2/60 p-1"
            >
              {audienceOrder.map((aud) => {
                const isActive = aud === active;
                return (
                  <button
                    key={aud}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleSelect(aud)}
                    className={cn(
                      "rounded-full px-4 py-1.5 text-[13px] font-medium tracking-tight transition-colors",
                      isActive ? "bg-ink text-page-bg" : "text-ink-2 hover:text-ink",
                    )}
                  >
                    {t(`landing.process.trackLabels.${aud}`)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div key={active} className="mt-8 grid grid-cols-1 border-t border-line md:grid-cols-3">
          {steps.map((s, i) => (
            <div
              key={`${active}-${i}`}
              className={cn(
                "border-line p-8 lg:p-10",
                i < steps.length - 1 ? "border-b md:border-r md:border-b-0" : "",
                "transition-all duration-700",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
              )}
              style={{ transitionDelay: isVisible ? `${i * 100}ms` : "0ms" }}
            >
              <div className="mono text-[12px] tracking-[0.14em] text-accent">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-3 text-[22px] leading-snug font-medium tracking-[-0.02em] text-ink">
                {s.title}
              </h3>
              <p className="mt-3 max-w-[40ch] text-[14px] leading-[1.65] text-ink-2">{s.body}</p>
              <div className="mt-5 space-y-1.5">
                {s.bullets.map((b) => (
                  <div key={b} className="mono text-[12px] leading-relaxed text-accent">
                    ↳ {b}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to={localizedHref("/process")}
            className="text-[15px] font-medium text-accent transition-colors hover:text-ink"
          >
            {t("landing.process.moreLink")}
          </Link>
        </div>
      </div>
    </section>
  );
}
